import { z } from "zod";
import { availableFonts } from "../constants";
import { mediaObjectKeySchema } from "./media";
import { RatingType, satisfactionFormSchema } from "./satisfactionForm";

const dochatConfigIdMissingStr = "Dochat config id cannot be empty";

const sessionIdInvalidLength = "Session id must be 24 characters long";

export const dochatConfigIdSchema = z
  .string({
    required_error: dochatConfigIdMissingStr,
  })
  .trim()
  .min(1, { message: dochatConfigIdMissingStr })
  .uuid();

export const localeSchema = z.string().min(2).max(5);

export const buttonPositionParamSchema = z.enum(["default", "bottom"]);

export const positionParamSchema = z.enum(["top", "bottom"]);

export const disclaimerMessagePlacementParamSchema = z.enum(["settings", "footer"]);

export const textParamSchema = z.string().max(2048);

export const titleParamSchema = z.string().max(256);

export const localeParamSchema = z.string().min(1).max(5);

export const rasaUrlParamSchema = z.string().min(1).max(1048);

// The image a chat or a widget displays, as delivered to do-chat and the widget: a legacy data
// URI or, since ADR-0015, the imgproxy URL crai-office signs from the object key stored beside
// the URL (`logoKey`, `fullLogoKey`, `avatarKey`, `avatarCloseKey`). The key is the source of truth; the URL
// is derived from it on every save. Data URIs are still read until the stored themes are migrated
// (follow-up: drop them and the 100 kB ceiling, see docs/cloudinary-replacement).
export const logoDataUriParamSchema = z.string().url().min(1).max(102_400);

/** Header logo shown by the chat: the square symbol (`logoDataUri`) or the full horizontal one. */
export const logoTypeParamSchema = z.enum(["symbol", "full"]);

/** Height of the full logo in the header, in px. */
export const fullLogoHeightParamSchema = z.number().int().min(16).max(64);

const localeConfigSchema = z
  .object({
    title: titleParamSchema,
    placeholder: textParamSchema,
    subTitle: titleParamSchema.optional(),
    disclaimerText: textParamSchema.nullish(),
    backButtonLabel: z.string().min(1).max(256).nullish(),
    rasaUrl: rasaUrlParamSchema.optional(),
    isDefault: z.boolean().optional(),
  })
  .passthrough();

export const conversationContextSchema = z
  .object({
    brand: z.string(),
    availableLocales: z.record(localeParamSchema, localeConfigSchema).nullable(),
    data: z.record(z.string(), z.unknown()).nullable(),
    entities: z.record(z.string(), z.unknown()).nullable(),
    externalRef: z.string().nullable(),
    intent: z.string().nullable(),
    language: z.string(),
    startMessage: z.string(),
  })
  .partial();

export const availableLocalesSchema = z.record(localeParamSchema, localeConfigSchema);

const urlRewriteHostStringSchema = z
  .string()
  .min(1)
  .refine((h) => /^(\*\.)?[a-z0-9.-]+$/i.test(h), {
    message: "zod-custom:doChat.urlRewrite.invalidHost",
  });

const urlRewritePathPatternSchema = z.string().refine(
  (p) => {
    try {
      new RegExp(p);
      return true;
    } catch {
      return false;
    }
  },
  { message: "zod-custom:doChat.urlRewrite.invalidPathPattern" }
);

/**
 * Order matters for do-config: its PATCH validator is this schema as JSON schema (an `anyOf`), run
 * by ajv with `removeAdditional`. A branch whose `required` keys an object already has strips the
 * object's other keys even when the branch then fails, so `equals` before `numberCompare` stripped
 * `operator`/`value` and the PATCH got a 400. `numberCompare` first, `exists` last.
 */
export const urlRewriteContextConditionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("numberCompare"),
    key: z.string().min(1),
    operator: z.enum(["<", "<=", ">", ">="]),
    value: z.number(),
  }),
  z.object({
    type: z.literal("equals"),
    key: z.string().min(1),
    value: z.string(),
  }),
  z.object({
    type: z.literal("in"),
    key: z.string().min(1),
    values: z.array(z.string()).min(1),
  }),
  z.object({
    type: z.literal("exists"),
    key: z.string().min(1),
  }),
]);

export const urlRewriteMatchCriteriaSchema = z.object({
  host: urlRewriteHostStringSchema.optional(),
  pathPattern: urlRewritePathPatternSchema.optional(),
  queryContains: z.record(z.string(), z.string()).optional(),
  allUrls: z.boolean().optional(),
  context: z.array(urlRewriteContextConditionSchema).min(1).optional(),
});

/**
 * A node of an `insertContextBlob` template: a string (literal, or a
 * `{{contextKey}}` placeholder), a nested object or an array of nodes.
 */
export type UrlRewriteBlobTemplateNode =
  | string
  | UrlRewriteBlobTemplateNode[]
  | { [key: string]: UrlRewriteBlobTemplateNode };

const urlRewriteBlobTemplateNodeSchema: z.ZodType<UrlRewriteBlobTemplateNode> = z.lazy(() =>
  z.union([
    z.string(),
    z.array(urlRewriteBlobTemplateNodeSchema),
    z.record(z.string(), urlRewriteBlobTemplateNodeSchema),
  ])
);

export const urlRewriteBlobTemplateSchema = z
  .record(z.string(), urlRewriteBlobTemplateNodeSchema)
  .refine((template) => Object.keys(template).length > 0, {
    message: "zod-custom:doChat.urlRewrite.emptyContextBlobTemplate",
  });

export const urlRewriteActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("appendParam"),
    key: z.string().min(1),
    value: z.string(),
  }),
  z.object({
    type: z.literal("overwriteParam"),
    key: z.string().min(1),
    value: z.string(),
  }),
  z.object({
    type: z.literal("removeParam"),
    key: z.string().min(1),
  }),
  z.object({
    type: z.literal("transferParams"),
    keys: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("rewriteKey"),
    sourceKey: z.string().min(1),
    targetKey: z.string().min(1),
  }),
  z.object({
    type: z.literal("insertContextValue"),
    contextKey: z.string().min(1),
    fallback: z.string().optional(),
    target: z.discriminatedUnion("type", [
      z.object({ type: z.literal("queryParam"), key: z.string().min(1) }),
      z.object({ type: z.literal("pathPlaceholder"), placeholder: z.string().min(1) }),
    ]),
  }),
  z.object({
    type: z.literal("insertContextBlob"),
    // Exactly one of `contextKeys` / `template` is required. That check lives in
    // `urlRewriteRuleSchema` because a discriminated union member cannot be refined.
    contextKeys: z.array(z.string().min(1)).min(1).optional(),
    template: urlRewriteBlobTemplateSchema.optional(),
    targetParam: z.string().min(1),
    encoding: z.literal("base64Json"),
  }),
  z.object({
    type: z.literal("overwriteUrl"),
    url: z.string().url(),
  }),
  z.object({
    type: z.literal("appendPathSegment"),
    contextKey: z.string().min(1),
  }),
]);

export const urlRewriteRuleSchema = z
  .object({
    match: urlRewriteMatchCriteriaSchema,
    actions: z.array(urlRewriteActionSchema).min(1),
    stopOnMatch: z.boolean().optional(),
    /**
     * When false, the rule is skipped at runtime without being deleted. Useful
     * for temporarily disabling a rule from a future UI. Defaults to true.
     */
    enabled: z.boolean().optional(),
  })
  .superRefine((rule, ctx) => {
    const hasScope = !!rule.match.host || !!rule.match.pathPattern || !!rule.match.queryContains;
    const hasUnsafeAction = rule.actions.some(
      (a) => a.type === "transferParams" || a.type === "rewriteKey"
    );
    if (hasUnsafeAction && (rule.match.allUrls || !hasScope)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "zod-custom:doChat.urlRewrite.unsafeActionRequiresScope",
      });
    }
    rule.actions.forEach((action, index) => {
      if (action.type !== "insertContextBlob") return;
      if (action.contextKeys && action.template) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["actions", index],
          message: "zod-custom:doChat.urlRewrite.contextBlobKeysAndTemplateExclusive",
        });
      } else if (!action.contextKeys && !action.template) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["actions", index],
          message: "zod-custom:doChat.urlRewrite.contextBlobRequiresKeysOrTemplate",
        });
      }
    });
  });

export const urlRewriteConfigSchema = z.object({
  rules: z.array(urlRewriteRuleSchema),
  /**
   * Kill switch for the whole config. When false, every outbound URL is
   * returned unchanged regardless of the rules. Defaults to true.
   */
  enabled: z.boolean().optional(),
});

const chatbotParamsBaseSchema = z
  .object({
    title: titleParamSchema,
    placeholder: textParamSchema,
    disclaimerText: textParamSchema,
    subTitle: titleParamSchema,
    availableLocales: z.record(localeParamSchema, localeConfigSchema).nullable(),
    theme: z.record(z.string(), z.string()).nullable(),
    buttons: z
      .object({
        position: buttonPositionParamSchema,
        backButtonPosition: z.enum(["actions", "buttons"]).default("actions"),
        backButtonIconUri: z.string().url().optional(),
        backButtonIconName: z.string().optional(),
        showOnlyLast: z.boolean(),
      })
      .passthrough(),
    staticContextsMap: z.record(z.string(), z.unknown()),
    rasaContext: z.record(z.string(), conversationContextSchema.passthrough()).nullable(),
    fontId: z.nativeEnum(availableFonts).optional(),
    fonts: z.array(z.nativeEnum(availableFonts)).optional(),
    customFonts: z.record(z.string(), z.string().url()).optional(),
    lastMessageScrollPosition: positionParamSchema,
    defaultModelLanguage: localeParamSchema,
    disclaimerActivate: z.boolean(),
    disclaimerMessagePlacement: disclaimerMessagePlacementParamSchema,
    enableCloseButton: z.boolean(),
    enableRefreshButton: z.boolean(),
    logoDataUri: logoDataUriParamSchema,
    // Object key of the logo in the media bucket, from which logoDataUri is signed
    logoKey: mediaObjectKeySchema.nullable(),
    logoType: logoTypeParamSchema,
    fullLogoDataUri: logoDataUriParamSchema,
    // Object key of the full logo in the media bucket, from which fullLogoDataUri is signed
    fullLogoKey: mediaObjectKeySchema.nullable(),
    fullLogoHeight: fullLogoHeightParamSchema,
    noUserInput: z.boolean(),
    onlyLastMessage: z.boolean(),
    showPoweredByDialonce: z.boolean(),
    sttEnabled: z.boolean(),
    ttsEnabled: z.boolean(),
    enableCopyMessageButton: z.boolean(),
    scrollOnTopOfUserMessages: z.boolean(),
    noHeader: z.boolean(),
    motionEnabled: z.boolean(),
    simulateStreaming: z.boolean(),
    handover: z
      .object({
        displayAfterNbMsg: z.number().min(0).max(10),
        handoverStartCuid: z.string().min(12).max(12),
      })
      .nullable(),
    urlRewriteConfig: urlRewriteConfigSchema.nullable(),
  })
  .partial()
  .passthrough();

const recursiveChatbotParamsSchema = chatbotParamsBaseSchema.extend({
  fullSize: z.lazy(() => chatbotParamsBaseSchema).optional(),
});

export const chatbotParamsSchema = recursiveChatbotParamsSchema.transform((chatbotParams) => {
  const { title, subTitle, placeholder, disclaimerText, availableLocales, ...restChatbotParams } =
    chatbotParams;

  if (availableLocales) {
    return { ...restChatbotParams, availableLocales };
  }

  return chatbotParams;
});

// The display part of the chatbot parameters: theme, colours, logo, texts, language and widget
// options. It is what a chat that requires sign-in shows before the user is signed in, so it
// strips everything else, including the per-locale `rasaUrl` and any unknown key.
const displayLocaleConfigSchema = localeConfigSchema.omit({ rasaUrl: true }).strip();

const chatbotDisplayParamsBaseSchema = chatbotParamsBaseSchema
  .pick({
    title: true,
    placeholder: true,
    disclaimerText: true,
    subTitle: true,
    theme: true,
    buttons: true,
    fontId: true,
    fonts: true,
    customFonts: true,
    lastMessageScrollPosition: true,
    defaultModelLanguage: true,
    disclaimerActivate: true,
    disclaimerMessagePlacement: true,
    enableCloseButton: true,
    enableRefreshButton: true,
    logoDataUri: true,
    logoType: true,
    fullLogoDataUri: true,
    fullLogoHeight: true,
    noUserInput: true,
    onlyLastMessage: true,
    showPoweredByDialonce: true,
    sttEnabled: true,
    ttsEnabled: true,
    enableCopyMessageButton: true,
    scrollOnTopOfUserMessages: true,
    noHeader: true,
    motionEnabled: true,
    simulateStreaming: true,
  })
  .extend({
    availableLocales: z.record(localeParamSchema, displayLocaleConfigSchema).nullable().optional(),
  })
  .strip();

export const chatbotDisplayParamsSchema = chatbotDisplayParamsBaseSchema
  .extend({ fullSize: chatbotDisplayParamsBaseSchema.optional() })
  .transform((chatbotParams) => {
    // Same rule as `chatbotParamsSchema`: per-locale texts replace the root ones.
    const { title, subTitle, placeholder, disclaimerText, availableLocales, ...restChatbotParams } =
      chatbotParams;

    if (availableLocales) {
      return { ...restChatbotParams, availableLocales };
    }

    return chatbotParams;
  });

export const chatbotParamsLocalSchema = z.object({
  availableLocales: z.record(localeParamSchema, localeConfigSchema).nullable(),
  defaultLanguage: localeSchema.nullish(),
});

export const sessionIdSchema = z.string().length(24, { message: sessionIdInvalidLength });

export const APIDochatConfigSchema = z
  .object({
    brandFromQParams: z.string().nullish(),
    callingURL: z.string().nullish(),
    chatbotStaticCtxMapKey: z.string().nullish(),
    digitalDynamicCtx: conversationContextSchema.passthrough().nullish(),
    id: dochatConfigIdSchema,
    pinCode: z.string().nullish(),
    positionId: z.string().nullish(),
    sessionId: sessionIdSchema.nullish(),
    theming: z.string().nullish(),
    userLang: z.string().nullish(),
  })
  .required();

export const APIDochatConfigWithoutSessionSchema = z
  .object({ id: dochatConfigIdSchema })
  .required();

export const APIChatbotParamsSchema = z.object({
  id: dochatConfigIdSchema,
  chatbotParams: chatbotParamsSchema,
});

export const APIConversationStartParamsSchema = z
  .object({
    analyticId: z.string().uuid(),
    chatbotParams: chatbotParamsSchema,
    company_cuid: z.string().min(1),
    computedAvailableLocales: availableLocalesSchema.nullish(),
    computedBrand: z.string().nullish(),
    computedLanguage: z.string().nullish(),
    computedStartMessage: z.string(),
    computedStaticData: z.record(z.string(), z.unknown()).nullable(),
    createdPinCode: z.string().nullish(),
    entities: z.record(z.string(), z.unknown()).nullish(),
    id: dochatConfigIdSchema,
    intent: z.string(),
    maxUserMessageLength: z.number().optional(),
    mergedCtxData: z.record(z.string(), z.unknown()).nullish(),
    orchestrator_id: z.string().min(1),
    outOfCredits: z.boolean().optional(),
    rasaUrl: z.string().min(1),
    redactPrivateInfos: z.boolean().optional(),
    satisfactionForm: satisfactionFormSchema.nullish(),
  })
  .required();

// Input of `GET /api/configs/<dochatConfigId>`: the id is a path segment, and the context
// parameters (the fields of `APIDochatConfigSchema` other than `id`) are query parameters, with
// `digitalDynamicCtx` JSON-encoded.
const configRouteQueryParamSchema = z.string().nullish().default(null);

export const APIDochatConfigRouteQuerySchema = z.object({
  dochatConfigId: z.string(),
  brandFromQParams: configRouteQueryParamSchema,
  callingURL: configRouteQueryParamSchema,
  chatbotStaticCtxMapKey: configRouteQueryParamSchema,
  digitalDynamicCtx: configRouteQueryParamSchema,
  pinCode: configRouteQueryParamSchema,
  positionId: configRouteQueryParamSchema,
  sessionId: configRouteQueryParamSchema,
  theming: configRouteQueryParamSchema,
  userLang: configRouteQueryParamSchema,
});

// The four do-chat configuration fields that require users to sign in.
export const dochatAuthConfigFieldsSchema = z.object({
  // The chat requires sign-in, through the tenant's SSO or the Dial Once SSO.
  requireOAuth: z.boolean(),
  // Alias of the customer identity provider in the `DialOnce` realm, `c-<companyCuid>-<slug>`.
  // `null` means no tenant SSO: users sign in with the Dial Once SSO.
  oauthIdpAlias: z.string().min(1).nullable(),
  // Display name of that identity provider, copied from Keycloak when the configuration is
  // saved: the label of the login screen's customer button. `null` without `oauthIdpAlias`.
  oauthIdpDisplayName: z.string().min(1).nullable(),
  // Hides the "Sign in with Dial Once" button. Staff sign-in stays accepted.
  oauthHideStaffLink: z.boolean(),
});

export const dochatAuthStatusSchema = z.enum(["open", "required", "authenticated", "forbidden"]);

// The only failure codes the browser ever receives. Internal reasons stay in the logs.
export const dochatAuthPublicReasonSchema = z.enum([
  "access_denied",
  "unavailable",
  "sign_in_failed",
  "cancelled",
  "expired",
  "not_opened_by_chat",
  "ticket_expired",
  "rate_limited",
]);

export const dochatAuthProviderSchema = z.enum(["customer", "dialonce"]);

export const dochatAuthSchema = z.object({
  required: z.boolean(),
  status: dochatAuthStatusSchema,
  reason: dochatAuthPublicReasonSchema.optional(),
  // The canonical host of the sign-in popup.
  authUrl: z.string().url().nullable(),
  providers: z.array(dochatAuthProviderSchema),
  // The display name of the customer identity provider, when `providers` offers it.
  customerProviderName: z.string().min(1).nullable().optional(),
});

// Display configuration, always present: the chat applies its theme before anything else.
export const dochatDisplayConfigSchema = z.object({
  id: dochatConfigIdSchema,
  chatbotParams: chatbotDisplayParamsSchema,
});

// The 200 answer of `GET /api/configs/<dochatConfigId>`. Whether the chat may start is in the
// body, not in the status code: `session` is present exactly when the status is `open` or
// `authenticated`.
export const APIDochatConfigAnswerSchema = z
  .object({
    config: dochatDisplayConfigSchema,
    auth: dochatAuthSchema,
    session: APIConversationStartParamsSchema.optional(),
  })
  .superRefine(({ auth, session }, ctx) => {
    const mayStart = auth.status === "open" || auth.status === "authenticated";
    if (mayStart !== (session !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["session"],
        message: mayStart ? "session is required" : "session is not allowed",
      });
    }
    if (auth.required !== (auth.status !== "open")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["auth", "required"],
        message: "only an open chat does not require sign-in",
      });
    }
    if (auth.status === "forbidden" && !auth.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["auth", "reason"],
        message: "a forbidden answer carries its public reason",
      });
    }
  });

export const ratingTypeSchema = z.nativeEnum(RatingType);

// The user's answer to a satisfaction survey. Built in two steps: first the rating,
// then (optionally) the written feedback. `feedbackText` is therefore optional.
export const surveyAnswerSchema = z.object({
  ratingType: ratingTypeSchema,
  ratingValue: z.number(),
  feedbackText: z.string().max(2048).optional(),
});

export const APISubmitSurveySchema = z.object({
  analyticId: z.string().uuid(),
  surveyAnswer: surveyAnswerSchema,
});

export const contrackResponseSchema = z.object({
  context: conversationContextSchema,
});

export const ConfigTypeEnum = {
  production: "production",
  sandbox: "sandbox",
  test: "test",
} as const;

export const availableDoChatConfigTypeSchema = z.union([
  z.literal(ConfigTypeEnum.production),
  z.literal(ConfigTypeEnum.sandbox),
  z.literal(ConfigTypeEnum.test),
]);

export const configDoChatSchema = z.object({
  rasaUrl: rasaUrlParamSchema.optional(),
  chatbotv2: z.boolean().nullish(),
  chatbotParams: chatbotParamsSchema,
  configType: availableDoChatConfigTypeSchema,
  uuid: z.string(),
  redactPrivateInfos: z.boolean().optional(),
  type: availableDoChatConfigTypeSchema,
  ...dochatAuthConfigFieldsSchema.partial().shape,
});

export type ChatbotParams = z.infer<typeof chatbotParamsSchema>;
export type UrlRewriteConfig = z.infer<typeof urlRewriteConfigSchema>;
export type UrlRewriteRule = z.infer<typeof urlRewriteRuleSchema>;
export type UrlRewriteMatchCriteria = z.infer<typeof urlRewriteMatchCriteriaSchema>;
export type UrlRewriteAction = z.infer<typeof urlRewriteActionSchema>;
export type UrlRewriteContextCondition = z.infer<typeof urlRewriteContextConditionSchema>;
export type ConversationContext = z.infer<typeof conversationContextSchema>;
export type ConversationStartParams = z.infer<typeof APIConversationStartParamsSchema>;
export type AvailableLocales = z.infer<typeof availableLocalesSchema>;
export type LocaleConfig = z.infer<typeof localeConfigSchema>;
export type ChatbotParamsLocal = z.infer<typeof chatbotParamsLocalSchema>;
export type Locale = z.infer<typeof localeSchema>;
export type APIDochatConfig = z.infer<typeof APIDochatConfigSchema>;
export type DoChatConfig = z.infer<typeof configDoChatSchema>;
export type SurveyAnswer = z.infer<typeof surveyAnswerSchema>;
export type APISubmitSurvey = z.infer<typeof APISubmitSurveySchema>;
export type ChatbotDisplayParams = z.infer<typeof chatbotDisplayParamsSchema>;
export type APIDochatConfigRouteQuery = z.infer<typeof APIDochatConfigRouteQuerySchema>;
export type DochatAuthConfigFields = z.infer<typeof dochatAuthConfigFieldsSchema>;
export type DochatAuthStatus = z.infer<typeof dochatAuthStatusSchema>;
export type DochatAuthPublicReason = z.infer<typeof dochatAuthPublicReasonSchema>;
export type DochatAuthProvider = z.infer<typeof dochatAuthProviderSchema>;
export type DochatAuth = z.infer<typeof dochatAuthSchema>;
export type DochatDisplayConfig = z.infer<typeof dochatDisplayConfigSchema>;
export type APIDochatConfigAnswer = z.infer<typeof APIDochatConfigAnswerSchema>;
